import { DateTime, Duration, DurationObjectUnits } from 'luxon';
import { RentalProductPrice } from '../clients/quipli-service/pricing/pricing';
import Dinero, { Currency } from 'dinero.js';
import { PaginationRequest } from '../clients/quipli-service/pagination/pagination';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { UserAddress } from '../clients/quipli-service/user/user';
import _ from 'lodash';

export function addNumericSuffix(n: number): string {
  const x = n % 10;
  const y = n % 100;
  if (x == 1 && y != 11) {
    return `${n}st`;
  } else if (x == 2 && y != 12) {
    return `${n}nd`;
  } else if (x == 3 && y != 13) {
    return `${n}rd`;
  } else return `${n}th`;
}

export function displayMoneyFormat(n?: number, currency?: string): string {
  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  });
  return formatter.format(n || 0);
}

export function detectTouch(): boolean {
  return 'ontouchstart' in window || window.navigator.maxTouchPoints > 0;
}

export function convertDurationForRangePriceUnits(price: RentalProductPrice, prevPrice?: RentalProductPrice): string {
  const lowerBound = (prevPrice?.durationValue ?? 0) + 1;
  if (
    price.durationValue === 1 ||
    (prevPrice && prevPrice.durationUnit === price.durationUnit && lowerBound === price.durationValue)
  ) {
    return `${price.durationValue} ${price.durationUnit}${price.durationValue > 1 ? 's' : ''}`;
  } else if (prevPrice && prevPrice.durationUnit === price.durationUnit) {
    return `${lowerBound} - ${price.durationValue} ${price.durationUnit}${price.durationValue > 1 ? 's' : ''}`;
  } else {
    return `1 - ${price.durationValue} ${price.durationUnit}${price.durationValue > 1 ? 's' : ''}`;
  }
}

export function formatPhoneNumber(phoneNumberString: string | undefined): string {
  if (!phoneNumberString) return '';

  try {
    const phoneNumber = parsePhoneNumberWithError(phoneNumberString, 'US');

    if (!phoneNumber.country || phoneNumber.country === 'US') {
      return phoneNumber.formatNational();
    } else {
      return phoneNumber.formatInternational();
    }
  } catch (e) {
    return phoneNumberString;
  }
}

export function displayHumanDuration(
  d: Duration,
  units: (keyof DurationObjectUnits)[] = ['years', 'months', 'days', 'hours', 'minutes', 'seconds'],
) {
  const durationObject = d.shiftTo(...units).toObject();

  if (durationObject.years === 0) {
    durationObject.years = undefined;
  }

  if (durationObject.months === 0) {
    durationObject.months = undefined;
  }

  if (durationObject.days === 0) {
    durationObject.days = undefined;
  }

  if (durationObject.hours === 0) {
    durationObject.hours = undefined;
  }

  if (durationObject.minutes === 0) {
    durationObject.minutes = undefined;
  }

  if (durationObject.seconds === 0) {
    durationObject.seconds = undefined;
  }

  return Duration.fromObject(durationObject).toHuman();
}

export function validateFalsy(value?: number): boolean {
  return !!value || value === 0;
}

export function safeRound(value: number, precision?: number): number {
  if (isNaN(value)) {
    return 0;
  }

  // Adding Number.EPSILON to avoid floating point issues
  return _.round(value + Number.EPSILON, precision);
}

export function convertToMoney(amount: number, currency: Currency): Dinero.Dinero {
  return Dinero({ amount: +(+(+amount).toFixed(2) * 100).toFixed(0), currency });
}

export function dineroSum(values: Dinero.Dinero[], currency: Dinero.Currency): Dinero.Dinero {
  if (values.length > 0) {
    const [v, ...remaining] = values;
    let sum = Dinero({ amount: v.getAmount(), precision: v.getPrecision(), currency: v.getCurrency() });
    remaining.forEach((ov) => {
      sum = sum.add(ov);
    });
    return sum;
  } else {
    return Dinero({ currency });
  }
}

export async function withRetries<T>(b: () => Promise<T>, retries: number): Promise<T> {
  let lastError;
  for (let attempts = 0; attempts <= retries; attempts++) {
    try {
      const result = await b();
      return result;
    } catch (e: any) {
      lastError = e;
    }
  }
  throw lastError;
}

export function truncateString(s: string | undefined, maxLength: number): string {
  if (!s) return '';

  return s.length > maxLength ? `${s.substring(0, maxLength)}...` : s;
}

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

export type ArrayFields<T> = {
  [Property in keyof T as `${string & Property}Collection`]: Array<T[Property]>;
};

export type GenericSearchRequest<T> = Partial<T> & Partial<ArrayFields<T>>;
export type PaginatedGenericSearchRequest<T> = GenericSearchRequest<T> & PaginationRequest;

export type RequiredFields<T, P extends keyof T> = T & Required<Pick<T, P>>;

export function formatAddress(address: UserAddress): string {
  let addressText = address.address1;
  if (!address.isGoogleGenerated) {
    addressText += `${address.address2 ? `, ${address.address2}` : ''}${address.city ? `, ${address.city}` : ''}${
      address.state ? `, ${address.state}` : ''
    }${address.zip ? `, ${address.zip}` : ''}`;
  }

  return addressText;
}

export function convertFiletoBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result?.toString() || '');
    reader.onerror = (error) => reject(error);
  });
}

export const parseIsoDate = (input: string, timezone?: string): DateTime => {
  if (!timezone) {
    return DateTime.fromISO(input, { setZone: true });
  }
  return DateTime.fromISO(input, { zone: timezone });
};

export const parseOptionalIsoDate = (input: string | undefined, timezone?: string): DateTime | undefined => {
  return input ? parseIsoDate(input, timezone) : undefined;
};
